import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';

import {css, html, LitElement} from 'lit';
import {customElement, property, query, state} from 'lit/decorators.js';
import {SlDialog} from '@shoelace-style/shoelace';
import {styleMap} from 'lit-html/directives/style-map.js';
import {TemplateResult} from 'lit-html';
import {when} from "lit/directives/when.js";
import {classMap} from 'lit-html/directives/class-map.js';
import {DirectiveResult} from "lit/directive.js";

@customElement('sp-dialog')
export class SpDialog extends LitElement {
    @property()
    allowOverflow = false;
    @property({type: Boolean})
    fullHeight: boolean;
    // Define where the dialog component should be attached to the DOM (default is body)
    @property()
    renderTarget: Element = document.body;
    @property()
    template: SpDialogTemplate;
    @property({type: Boolean})
    defaultClearContentOnHide = true;
    @property()
    dialogName = "default";
    // Suppress attachment of a searchParam to the route on show
    @property({type: Boolean})
    suppressUrlState = true;
    @state()
    destroy = false;

    renderTargetInstance: SpDialog;
    originalDialogInstance: SpDialog;
    popStateHandler = this.syncStateWithQueryParam.bind(this);

    @query('sl-dialog') slDialog: SlDialog;

    static styles = [
        //language=css
        css`
            sl-dialog::part(panel) {
                padding: 0;
                max-width: calc(100% - calc(2 * var(--sp-default-padding)));
                max-height: calc(100% - calc(2 * var(--sp-default-padding)));
                overflow: hidden;
                border-radius: 0;
            }

            sl-dialog::part(body) {
                padding: 0;
                overflow: hidden;
            }
            
            .allowOverflow::part(panel),
            .allowOverflow::part(body)
            {
                overflow: visible;
            }                
        `
    ];

    emitEvent(type: 'show' | 'after-show' | 'hide' | 'after-hide'): void {
        const url = new URL(window.location.href);
        if (!this.suppressUrlState) {
            if (type === 'show' && !this.hasSearchParam(url)) {
                window.addEventListener('popstate', this.popStateHandler);
                if (!this.hasSearchParam(url))
                    this.setSearchParam(url);
            }
            if (type === 'hide' && this.hasSearchParam(url)) {
                window.removeEventListener('popstate', this.popStateHandler);
                if (this.hasSearchParam(url))
                    this.removeSearchParam(url);
            }
        }
        this.dispatchEvent(
            new CustomEvent(type, { bubbles: true, composed: true })
        );
    }

    getElementFromDialogById(id: string) {
        return this.getDialog().shadowRoot.querySelector('slot').assignedElements()[0].querySelector(`#${id}`);
    }

    // Returns a promise that fulfills when sp-dialog has opened
    async show(): Promise<void> {
        this.destroy = false;
        return this.getDialog().show();
    }

    async hide(clearContent = this.defaultClearContentOnHide): Promise<void> {
        const promise = this.getDialog().hide();
        promise.then(val => { if (clearContent) this.destroy = true });
        return promise;
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (this.renderTarget) this.renderTarget.removeChild(this.renderTargetInstance);
    }

    handleCloseViaOverlay(event) {
        if (event.detail.source === 'overlay' && this.defaultClearContentOnHide) {
            this.hide();
        }
    }

    getTemplate() {
        return html`
            <sl-dialog 
                    class=${ classMap({ allowOverflow: this.allowOverflow }) }
                    no-header
                    @sl-request-close=${ event => { this.handleCloseViaOverlay(event) } }
                    @sl-show=${ () => this.emitEvent('show') }
                    @sl-after-show=${ () => this.emitEvent('after-show') }
                    @sl-hide=${ () => this.emitEvent('hide') }
                    @sl-after-hide=${ () => this.emitEvent('after-hide') }>
                <div style=${styleMap({
                        ...this.fullHeight && {height: 'calc(100vh - calc(2*var(--sp-default-padding)))'}
                    })}>
                    ${ when(!this.destroy, () => this.template) }
                </div>
            </sl-dialog>`;
    }

    protected getInstance() {
        return new SpDialog();
    }

    render() {
        if (this.renderTarget && !this.renderTargetInstance) {
            this.renderTargetInstance = this.getInstance();
            this.renderTargetInstance.renderTarget = undefined;
            this.renderTargetInstance.getTemplate = () => { return this.getTemplate() };
            this.renderTarget.appendChild(this.renderTargetInstance);
            this.renderTargetInstance.originalDialogInstance = this;
        } else if (this.renderTargetInstance) {
            this.renderTargetInstance.requestUpdate();
        }
        return html`<slot></slot>${ when(!this.renderTarget, () => this.getTemplate()) }`;
    }

    private hasSearchParam(url: URL) {
        return url.searchParams.has(`${ this.dialogName }-dialog`);
    }

    private setSearchParam(url: URL) {
        url.searchParams.append(`${ this.dialogName }-dialog`, '1');
        window.history.pushState("", document.title, url);
    }

    private removeSearchParam(url: URL) {
        url.searchParams.delete(`${ this.dialogName }-dialog`);
        window.history.pushState("", document.title, url);
    }

    private getDialog(): SlDialog {
        return this.renderTarget ? this.renderTargetInstance.slDialog : this.slDialog;
    }

    syncStateWithQueryParam() {
        const url = new URL(window.location.href);
        if (this.getDialog().open && !this.hasSearchParam(url)) {
            this.getDialog().hide();
        } else if (!this.getDialog().open && this.hasSearchParam(url)) {
            this.getDialog().show();
        }
    }
}

export type SpDialogTemplate = TemplateResult | DirectiveResult | string;
